import { MD5 } from '@/utils/MD5';

type SmarticoUserCredentials = {
  user_id: number;
  user_hash_smartico: string;
};

export const useSmarticoInit = defineStore(
  'smarticoInit',
  () => {
    const {
      isRequestPushPermission,
      gamificationActiveAndShow,
      isSmarticoActived,
      isAllowLocalhost,
      isSmarticoVisitorModeActived,
    } = storeToRefs(useSmarticoConfigs());

    const { brandId, labelId } = useRuntimeConfig().public.smartico;

    const { debugLog } = useSmarticoConfigs();

    const { loadUserInfosProfile } = useSmarticoUserInfos();

    const { fetchUserProfile, userId } = useUserData();

    const currentLang = computed(() => {
      return currentLocale() || useRuntimeConfig().public.brand.language;
    });

    const isReadySmartico = ref<boolean>(false);
    const isReadyGamefication = ref<boolean>(false);
    const isGameMenuOpen = ref<boolean>(false);
    const initSmarticoError = ref<boolean>(false);
    const isSuccessCheckUserIdentify = ref<boolean>(false);
    const loopMaxTimes = ref<number>(10);
    const activeIntervals = ref<ReturnType<typeof setInterval>[]>([]);
    const currentSmarticoMode = ref<'none' | 'visitor' | 'user'>('none');
    const isStartingSmartico = ref<boolean>(false);
    const isLogoutInProgress = ref<boolean>(false);
    const smarticoInitRunId = ref<number>(0);
    const areUserListenersRegistered = ref<boolean>(false);
    const userInitRetryCount = ref<number>(0);
    const maxUserInitRetries = 2;
    const smarticoSyncDelayMs = 1000;

    function logSmarticoEvent(
      event: string,
      payload?: Record<string, unknown>,
    ) {
      const prefix = '[Smartico][lifecycle]';

      if (payload === undefined) {
        console.info(`${prefix} ${event}`);

        return;
      }

      console.info(`${prefix} ${event}`, payload);
    }

    function snapshotSmarticoState(): Record<string, unknown> {
      const sdk: any = window._smartico;
      const tracker = sdk?.tracker;
      const initParams = sdk?.initParams;

      return {
        hasSdk: Boolean(sdk),
        smarticoUserId: window._smartico_user_id ?? null,
        smarticoUserHashPresent: Boolean(window._smartico_user_hash),
        smarticoLanguage: window._smartico_language ?? null,
        identify:
          typeof sdk?.checkSuccessfullyIdentify === 'function'
            ? sdk.checkSuccessfullyIdentify()
            : null,
        trackerInitialized: Boolean(tracker),
        trackerExtUserId:
          typeof tracker?.getExtUserId === 'function'
            ? tracker.getExtUserId()
            : null,
        initLabelApiKey: sdk?.initLabelApiKey ?? null,
        visitorMode: initParams?.visitor_mode ?? null,
        currentSmarticoMode: currentSmarticoMode.value,
        isSuccessCheckUserIdentify: isSuccessCheckUserIdentify.value,
      };
    }

    function forceResetSmarticoSdkState(reason: string) {
      const sdk: any = window._smartico;

      if (!sdk) {
        return;
      }

      logSmarticoEvent('force_reset_before', {
        reason,
        ...snapshotSmarticoState(),
      });

      try {
        if (typeof sdk.logout === 'function') {
          sdk.logout();
        }
      } catch (err) {
        console.warn('[Smartico] forceReset: logout() threw', err);
      }

      try {
        sdk.tracker = null;
        sdk.initLabelApiKey = null;

        if (sdk.initParams) {
          sdk.initParams.visitor_mode = false;
          sdk.initParams.generate_visit = false;
        }
      } catch (err) {
        console.warn('[Smartico] forceReset: failed to reset internals', err);
      }

      logSmarticoEvent('force_reset_after', snapshotSmarticoState());
    }

    watch(currentLang, (newLang) => {
      if (currentSmarticoMode.value !== 'none' && isReadySmartico.value) {
        window._smartico_language = newLang;
        debugLog('🌐 _smartico_language updated to', newLang);
      }
    });

    watch(userId, (newUserId, oldUserId) => {
      if (!isSmarticoActived.value) {
        return;
      }

      if (newUserId && (!oldUserId || !isSuccessCheckUserIdentify.value)) {
        setTimeout(() => {
          if (userId.value !== newUserId || isSuccessCheckUserIdentify.value) {
            return;
          }

          startSmartico();
        }, smarticoSyncDelayMs);

        return;
      }

      if (!newUserId && oldUserId && isSmarticoVisitorModeActived.value) {
        startSmartico();
      }
    });

    function getTargetSmarticoMode() {
      if (userId.value) {
        return 'user' as const;
      }

      if (isSmarticoVisitorModeActived.value) {
        return 'visitor' as const;
      }

      return 'none' as const;
    }

    function waitSyncDelay() {
      return new Promise<void>((resolve) => {
        setTimeout(() => {
          resolve();
        }, smarticoSyncDelayMs);
      });
    }

    function startSmartico() {
      if (!isSmarticoActived.value) {
        console.warn(
          '[Smartico] startSmartico aborted: Smartico is not active in the configuration.',
        );

        return;
      }

      if (!window._smartico) {
        console.warn(
          '[Smartico] startSmartico aborted: window._smartico not found. Make sure the script has been loaded.',
        );

        return;
      }

      if (typeof window._smartico.init !== 'function') {
        const maxRetries = 40;
        let retries = 0;
        const waitForSdk = setInterval(() => {
          retries++;
          if (typeof window._smartico?.init === 'function') {
            clearInterval(waitForSdk);
            startSmartico();
          } else if (retries >= maxRetries) {
            clearInterval(waitForSdk);
            console.warn(
              '[Smartico] SDK methods unavailable after waiting. Check that the Smartico library finished loading.',
            );
          }
        }, 150);

        registerInterval(waitForSdk);

        return;
      }

      if (isLogoutInProgress.value) {
        console.warn(
          '[Smartico] startSmartico skipped: logout is still in progress.',
        );

        return;
      }

      const targetMode = getTargetSmarticoMode();

      if (targetMode === 'none') {
        console.warn(
          '[Smartico] startSmartico skipped: no identified user and visitor mode is disabled.',
        );

        return;
      }

      if (isStartingSmartico.value) {
        debugLog('[Smartico] startSmartico skipped: init already running.');

        return;
      }

      if (currentSmarticoMode.value === targetMode) {
        if (
          targetMode === 'visitor' ||
          isReadySmartico.value ||
          isSuccessCheckUserIdentify.value
        ) {
          debugLog(
            `[Smartico] startSmartico skipped: mode "${targetMode}" already active.`,
          );

          return;
        }
      }

      isStartingSmartico.value = true;
      clearActiveIntervals();

      const runId = ++smarticoInitRunId.value;

      userInitRetryCount.value = 0;

      try {
        if (targetMode === 'visitor') {
          window._smartico_user_id = null;
          window._smartico_user_hash = null;
          window._smartico_language = currentLang.value;

          window._smartico.init(labelId, {
            brand_key: brandId,
            lang: currentLang.value,
            generate_visit: true,
          });
          currentSmarticoMode.value = 'visitor';
          isReadySmartico.value = true;
          isSuccessCheckUserIdentify.value = false;

          debugLog('🟦 window._smartico_visitor', true);

          if (isRequestPushPermission.value) {
            setTimeout(() => {
              window._smartico.requestPushPermissions();
            }, 2000);
          }

          isStartingSmartico.value = false;

          return;
        }

        currentSmarticoMode.value = 'user';

        initSmarticoScript(runId).catch((err) => {
          console.error('[Smartico] Failed to initialize user SDK:', err);
        });
      } catch (err) {
        isStartingSmartico.value = false;
        console.error('[Smartico] Failed to initialize SDK:', err);
      }
    }

    function clearActiveIntervals() {
      activeIntervals.value.forEach((interval) => clearInterval(interval));
      activeIntervals.value = [];
    }

    function registerInterval(interval: ReturnType<typeof setInterval>) {
      activeIntervals.value.push(interval);
    }

    function resetSmarticoState() {
      unregisterSmarticoUserListeners();
      isReadySmartico.value = false;
      isReadyGamefication.value = false;
      isSuccessCheckUserIdentify.value = false;
      initSmarticoError.value = false;
      isStartingSmartico.value = false;
      currentSmarticoMode.value = 'none';
      smarticoInitRunId.value = smarticoInitRunId.value + 1;
      areUserListenersRegistered.value = false;
      userInitRetryCount.value = 0;
    }

    function logoutSmartico(options?: { isUserInitiated?: boolean }) {
      const isUserInitiated = options?.isUserInitiated ?? true;

      if (!isSmarticoActived.value) {
        console.warn(
          '[Smartico] logoutSmartico aborted: Smartico is not active in the configuration.',
        );

        return true;
      }

      if (isLogoutInProgress.value) {
        console.warn(
          '[Smartico] logoutSmartico skipped: logout is already in progress.',
        );

        return false;
      }

      clearActiveIntervals();

      let isLogoutEventSent = false;

      try {
        if (isSuccessCheckUserIdentify.value && window._smartico?.logout) {
          isLogoutInProgress.value = true;
          window._smartico.logout();
          isLogoutEventSent = true;
          console.info(
            '[Smartico] Logout successful. "user_logged_out" event sent.',
          );
        } else if (!isSuccessCheckUserIdentify.value) {
          console.warn(
            '[Smartico] Logout skipped: user was not identified by Smartico (identify not completed). _smartico.logout() will not be called.',
          );
        } else if (!window._smartico?.logout) {
          console.warn(
            '[Smartico] Logout skipped: window._smartico.logout is not available.',
          );
        }
      } catch (err) {
        isLogoutInProgress.value = false;
        console.error('[Smartico] Error executing _smartico.logout():', err);
      }

      if (isLogoutEventSent) {
        setTimeout(() => {
          isLogoutInProgress.value = false;
        }, 1500);
      }

      resetSmarticoState();

      try {
        window._smartico_user_hash = null;
        window._smartico_user_id = null;
        window._smartico_language = null;
      } catch (err) {
        console.error('[Smartico] Error clearing user global variables:', err);
      }

      // So em logout iniciado pelo usuario — nao no "logout preventivo"
      // do auth store antes de cada login, que reaproveita o token.
      if (isUserInitiated) {
        try {
          window.ReactNativeWebView?.postMessage(
            JSON.stringify({ type: 'userLoggedOut' }),
          );
        } catch {
          // sem ponte ativa (rodando no browser), ignora
        }
      }

      return isLogoutEventSent;
    }

    async function fetchSmarticoUserCredentials() {
      const { ApiService } = useApiService();

      const { data } = await ApiService.get<SmarticoUserCredentials>(
        'bff/smartico-gamification/',
      );

      return data;
    }

    function buildLegacyUserHash(): string | false {
      const { saltKey } = useRuntimeConfig().public.smartico;
      const { slsk } = useRuntimeConfig().public.betBy;

      if (!saltKey || !slsk) return false;

      const k1 = saltKey.split('-');

      if (!(k1?.length && k1.length > 3)) return false;

      const hvtms = ~~(+new Date() / 1000) * 1000 + 24 * 3600 * 1000;
      const x = `${k1[0]}-${k1[1]}-${k1[2]}-${slsk}`;
      const m = (userId.value + ':' + x + ':' + hvtms).toLowerCase();

      return MD5(m) + ':' + hvtms;
    }

    function isSmarticoRunStale(runId: number) {
      return (
        runId !== smarticoInitRunId.value ||
        currentSmarticoMode.value !== 'user'
      );
    }

    function waitForUserId(runId: number) {
      return new Promise<boolean>((resolve) => {
        let countSetUser = 0;
        const trySetUser = setInterval(() => {
          countSetUser = countSetUser + 1;

          if (isSmarticoRunStale(runId)) {
            clearInterval(trySetUser);
            resolve(false);

            return;
          }

          if (countSetUser >= loopMaxTimes.value) {
            debugLog('❎ ~ smartico ~ user not logged');
            console.error(
              `[Smartico] User identification timeout: userId not available after ${loopMaxTimes.value} attempts. Check if login completed and useUserData().userId is being populated.`,
            );

            initSmarticoError.value = true;
            isStartingSmartico.value = false;

            clearInterval(trySetUser);
            resolve(false);

            return;
          }

          if (!userId.value) {
            debugLog('🔁 ~ user not logged ~ ', countSetUser);

            if (countSetUser === 3) {
              fetchUserProfile({ force: true });
            }

            return;
          }

          clearInterval(trySetUser);
          resolve(true);
        }, 300);

        registerInterval(trySetUser);
      });
    }

    function setSmarticoUserGlobals(
      credentials: SmarticoUserCredentials | null,
    ) {
      const legacyHash = credentials?.user_hash_smartico
        ? null
        : buildLegacyUserHash();

      if (!credentials?.user_hash_smartico && legacyHash) {
        debugLog('🟠 ~ Smartico BFF unavailable, using legacy MD5 hash');
      }

      window._smartico_user_hash =
        credentials?.user_hash_smartico ?? legacyHash ?? null;
      window._smartico_user_id = credentials?.user_id ?? userId.value;
      window._smartico_language = currentLang.value;

      if (isAllowLocalhost.value) {
        window._smartico_allow_localhost = true;
      }

      debugLog('✅ _smartico_user_id', Boolean(window._smartico_user_id));
      debugLog('✅ _smartico_user_hash', Boolean(window._smartico_user_hash));
    }

    async function refreshSmarticoUserProfile(props?: Partial<TUserProfile>) {
      try {
        await loadUserInfosProfile(props);
      } catch (err) {
        debugLog('🔴 ~ Error loading Smartico profile:', err);
      }
    }

    async function handleSmarticoIdentify(
      _errCode: unknown,
      props?: Partial<TUserProfile>,
    ) {
      await refreshSmarticoUserProfile(props);
    }

    async function handleSmarticoPropsChange(props?: Partial<TUserProfile>) {
      await refreshSmarticoUserProfile(props);
    }

    function registerSmarticoUserListeners() {
      if (areUserListenersRegistered.value || !window._smartico?.on) {
        return;
      }

      window._smartico.on('identify', handleSmarticoIdentify);
      window._smartico.on('props_change', handleSmarticoPropsChange);

      areUserListenersRegistered.value = true;
    }

    function unregisterSmarticoUserListeners() {
      if (!areUserListenersRegistered.value || !window._smartico?.off) {
        areUserListenersRegistered.value = false;

        return;
      }

      window._smartico.off('identify', handleSmarticoIdentify);
      window._smartico.off('props_change', handleSmarticoPropsChange);
      areUserListenersRegistered.value = false;
    }

    async function prepareSmarticoUser(runId: number) {
      const hasUserId = await waitForUserId(runId);

      if (!hasUserId || isSmarticoRunStale(runId)) {
        return false;
      }

      let credentials: SmarticoUserCredentials | null = null;

      try {
        credentials = await fetchSmarticoUserCredentials();
      } catch (err) {
        debugLog('🔴 ~ Error fetching Smartico credentials:', err);
      }

      if (isSmarticoRunStale(runId)) {
        return false;
      }

      setSmarticoUserGlobals(credentials);

      return Boolean(window._smartico_user_id);
    }

    async function initSmarticoScript(runId: number) {
      if (!isSmarticoActived.value) {
        return;
      }

      try {
        logSmarticoEvent('user_init_start', {
          runId,
          ...snapshotSmarticoState(),
        });

        const isUserPrepared = await prepareSmarticoUser(runId);

        if (
          !isUserPrepared ||
          isSmarticoRunStale(runId) ||
          !window._smartico_user_id
        ) {
          logSmarticoEvent('user_init_aborted', {
            runId,
            isUserPrepared,
            isStale: isSmarticoRunStale(runId),
            smarticoUserId: window._smartico_user_id ?? null,
          });

          return;
        }

        registerSmarticoUserListeners();

        const sdkAny: any = window._smartico;
        const trackerExtUserId =
          typeof sdkAny?.tracker?.getExtUserId === 'function'
            ? sdkAny.tracker.getExtUserId()
            : null;

        const isInVisitorState =
          Boolean(sdkAny?.tracker) &&
          (sdkAny?.initParams?.visitor_mode === true ||
            trackerExtUserId === 'Visitor' ||
            trackerExtUserId == null);

        if (isInVisitorState) {
          forceResetSmarticoSdkState('transition_visitor_to_user');
        }

        window._smartico.init(labelId, {
          brand_key: brandId,
        });

        debugLog('🔵 window._smartico_logged', true);
        logSmarticoEvent('user_init_called', {
          runId,
          ...snapshotSmarticoState(),
        });

        await waitSyncDelay();

        if (isSmarticoRunStale(runId)) {
          return;
        }

        checkToProceedSmartico(runId);
      } catch (err) {
        initSmarticoError.value = true;
        isStartingSmartico.value = false;
        console.error('[Smartico] Failed to identify user:', err);
      }
    }

    function retrySmarticoUserInit(runId: number, reason: string) {
      if (isSmarticoRunStale(runId)) {
        return;
      }

      if (userInitRetryCount.value >= maxUserInitRetries) {
        initSmarticoError.value = true;
        isStartingSmartico.value = false;
        console.error(
          `[Smartico] User init failed after ${maxUserInitRetries} retries (${reason}).`,
        );

        return;
      }

      userInitRetryCount.value = userInitRetryCount.value + 1;
      initSmarticoError.value = false;
      isReadySmartico.value = false;
      isSuccessCheckUserIdentify.value = false;
      isStartingSmartico.value = true;

      const retryRunId = ++smarticoInitRunId.value;
      const retryDelayMs = 400 * userInitRetryCount.value;

      logSmarticoEvent('user_init_retry_scheduled', {
        runId,
        retryRunId,
        retryAttempt: userInitRetryCount.value,
        reason,
        retryDelayMs,
      });

      forceResetSmarticoSdkState(`retry:${reason}`);

      setTimeout(() => {
        if (
          isSmarticoRunStale(retryRunId) ||
          !isSmarticoActived.value ||
          !userId.value
        ) {
          isStartingSmartico.value = false;

          return;
        }

        initSmarticoScript(retryRunId).catch((err) => {
          initSmarticoError.value = true;
          isStartingSmartico.value = false;
          console.error('[Smartico] Failed to retry user SDK init:', err);
        });
      }, retryDelayMs);
    }

    function checkToProceedSmartico(runId: number) {
      initSmarticoError.value = false;
      isReadySmartico.value = false;

      const countProceed = ref(0);

      const tryToProceed = setInterval(() => {
        if (isSmarticoRunStale(runId)) {
          clearInterval(tryToProceed);

          return;
        }

        const checkSuccessfullyIdentify = window._smartico
          ? window._smartico.checkSuccessfullyIdentify()
          : false;

        debugLog('🟨 ~ checkSuccessfullyIdentify', checkSuccessfullyIdentify);

        countProceed.value = countProceed.value + 1;

        if (countProceed.value >= loopMaxTimes.value) {
          debugLog('❌ ~ checkSuccessfullyIdentify', checkSuccessfullyIdentify);
          logSmarticoEvent('identify_timeout', {
            runId,
            attempts: countProceed.value,
            ...snapshotSmarticoState(),
          });
          clearInterval(tryToProceed);
          retrySmarticoUserInit(runId, 'identify_timeout');

          return;
        }

        if (checkSuccessfullyIdentify) {
          debugLog('🟩 ~ checkSuccessfullyIdentify', checkSuccessfullyIdentify);

          isSuccessCheckUserIdentify.value = true;
          userInitRetryCount.value = 0;

          if (isSuccessCheckUserIdentify.value) {
            refreshSmarticoUserProfile().catch((err) => {
              debugLog('🔴 ~ Error refreshing Smartico profile:', err);
            });
            initSmarticoGamefication();
            requestPushPermission();
            registerNativePushTokenIfPending();
          }

          isReadySmartico.value = true;
          isStartingSmartico.value = false;

          clearInterval(tryToProceed);
        }
      }, 300);

      registerInterval(tryToProceed);
    }

    function initSmarticoGamefication() {
      initSmarticoError.value = false;
      isReadyGamefication.value = false;

      if (!gamificationActiveAndShow.value) {
        return;
      }

      const countGamefication = ref(0);

      const tryGamefication = setInterval(async () => {
        debugLog('🟡 ~ tryingToStartGamefication');

        countGamefication.value = countGamefication.value + 1;

        if (countGamefication.value >= loopMaxTimes.value) {
          debugLog('❌ ~ gameficationStarted', false);
          console.error(
            `[Smartico] Gamification init timeout: failed to load gamification profile after ${loopMaxTimes.value} attempts.`,
          );

          initSmarticoError.value = true;

          clearInterval(tryGamefication);

          return;
        }

        const manualErrorTest = ref(false);

        if (isSuccessCheckUserIdentify.value && !manualErrorTest.value) {
          await loadUserInfosProfile();
          isReadyGamefication.value = true;

          debugLog('🟢 ~ gameficationStarted', true);
          clearInterval(tryGamefication);

          window._smartico.on('gf_closing', () => {
            document.body.style.overflow = '';
          });
        }
      }, 300);

      registerInterval(tryGamefication);
    }

    /**
     * Registra na Smartico o token FCM/APNs que o app wrapper entregou via
     * ponte JS (em `window.__nativePushToken`). No-op se nao estiver no app.
     * Push web (Service Worker) segue por outro caminho, nao afetado.
     */
    function registerNativePushTokenIfPending() {
      const pending = window.__nativePushToken;

      if (!pending) {
        return;
      }

      if (typeof window._smartico?.registerNativePushToken !== 'function') {
        debugLog('🟠 ~ registerNativePushToken indisponivel no SDK, pulando.');

        return;
      }

      try {
        window._smartico.registerNativePushToken(
          pending.token,
          pending.platform,
          pending.packageId,
        );
        debugLog('🔹 ~ registerNativePushToken chamado', {
          platform: pending.platform,
          packageId: pending.packageId,
        });

        try {
          window.ReactNativeWebView?.postMessage(
            JSON.stringify({
              type: 'smarticoPushTokenAck',
              payload: {
                status: 'registered',
                detail: {
                  sdkExtUserId: window._smartico?.tracker?.ext_user_id ?? null,
                  packageId: pending.packageId,
                  platform: pending.platform,
                },
              },
            }),
          );
        } catch {
          // sem ponte ativa, ignora
        }
      } catch (err) {
        // ACK negativo pra o app resetar o dedup e re-tentar no proximo
        // foreground/refresh. Sem isso a falha fica silenciosa.
        try {
          const errorMessage =
            err instanceof Error ? err.message : String(err ?? 'unknown');

          window.ReactNativeWebView?.postMessage(
            JSON.stringify({
              type: 'smarticoPushTokenAck',
              payload: {
                status: 'failed',
                detail: {
                  error: errorMessage,
                  packageId: pending.packageId,
                  platform: pending.platform,
                },
              },
            }),
          );
        } catch {
          // sem ponte ativa, ignora
        }
      }
    }

    function requestPushPermission() {
      if (isRequestPushPermission.value) {
        if (isDev() || isStage()) {
          debugLog('🔺 ~ pushPermissionDisabledOnDev');

          return;
        }

        const countRequest = ref(0);

        const tryRequest = setInterval(() => {
          debugLog('🔸 ~ tryingRequestPushPermission');

          countRequest.value = countRequest.value + 1;

          if (countRequest.value >= loopMaxTimes.value) {
            console.warn(
              `[Smartico] Push permissions timeout: user not identified after ${loopMaxTimes.value} attempts.`,
            );
            clearInterval(tryRequest);

            return;
          }

          if (isSuccessCheckUserIdentify.value) {
            debugLog('🔹 ~ requestPushPermissions');
            window._smartico.requestPushPermissions();

            clearInterval(tryRequest);
          }
        }, 300);

        registerInterval(tryRequest);
      }
    }

    function runAction(act: string) {
      if (!isReadySmartico.value) {
        console.warn(
          `[Smartico] runAction("${act}") skipped: SDK is not ready (isReadySmartico=false).`,
        );

        return;
      }

      try {
        window._smartico.dp(act);
      } catch (err: any) {
        console.error(`[Smartico] Error executing action "${act}":`, err);
      }
    }

    return {
      isReadyGamefication,
      isReadySmartico,
      isGameMenuOpen,
      initSmarticoError,
      isSuccessCheckUserIdentify,
      requestPushPermission,
      startSmartico,
      logoutSmartico,
      runAction,
    };
  },

  {},
);
